/**
 * Agent function form component for creating and editing agent functions.
 * Uses PageFormLayout with left sidebar navigation and CoordioCore card sections.
 * Supports Mustache variable templating in System Prompt linked to Input Schema properties.
 * Handles complex JSON Schemas with $ref, oneOf, $defs, and nested objects.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Collapse } from '@mui/material';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import {
  Copy as CopyIcon,
  CircleHelp as HelpIcon,
  ChevronDown as ExpandMoreIcon,
  ChevronUp as ExpandLessIcon,
  Braces as SectionIcon,
  Zap,
  Code,
} from 'lucide-react';
import { Button, Chip, CoordioDropdown, FormField, Input, Switch, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';
import { CodeEditor } from '../common/CodeEditor';
import { PageFormLayout } from '../layouts/PageFormLayout';
import { agentFunctionService } from '../../services/agentFunctionService';
import { useNotificationStore } from '../../store/notificationStore';
import { apiClient } from '../../services/api';
import type { PagedResult } from '../../types/tenant';
import type { AgentDefinitionDto } from '../../types/agent';
import type { AgentFunctionDetailDto } from '../../types/agentFunction';

function generateSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');
}

/** Represents a variable extracted from a JSON Schema. */
interface SchemaVariable {
  path: string;
  type: string;
  isSection: boolean;
  description?: string;
}

/**
 * Recursively extracts all variable paths from a JSON Schema string.
 * Resolves $ref pointers, merges oneOf/anyOf options, and traverses nested objects.
 * Returns flat list of { path, type, isSection } up to 4 levels deep.
 */
function extractSchemaVariables(schemaJson: string): SchemaVariable[] {
  try {
    const rootSchema = JSON.parse(schemaJson);
    if (!rootSchema || typeof rootSchema !== 'object') return [];

    const variables: SchemaVariable[] = [];

    function resolveRef(ref: string): Record<string, unknown> | null {
      if (!ref || !ref.startsWith('#/')) return null;
      const parts = ref.substring(2).split('/');
      let current: unknown = rootSchema;
      for (const part of parts) {
        if (!current || typeof current !== 'object') return null;
        current = (current as Record<string, unknown>)[part];
      }
      return (current as Record<string, unknown>) ?? null;
    }

    function resolve(node: unknown): Record<string, unknown> | null {
      if (!node || typeof node !== 'object') return null;
      const obj = node as Record<string, unknown>;
      if (typeof obj.$ref === 'string') {
        return resolveRef(obj.$ref);
      }
      return obj;
    }

    function collectProperties(node: unknown): Record<string, unknown> {
      const resolved = resolve(node);
      if (!resolved) return {};

      if (resolved.properties && typeof resolved.properties === 'object') {
        return { ...(resolved.properties as Record<string, unknown>) };
      }

      const merged: Record<string, unknown> = {};
      const options = (resolved.oneOf || resolved.anyOf) as unknown[] | undefined;
      if (Array.isArray(options)) {
        for (const option of options) {
          const optResolved = resolve(option);
          if (optResolved?.properties && typeof optResolved.properties === 'object') {
            Object.assign(merged, optResolved.properties as Record<string, unknown>);
          }
        }
      }
      return merged;
    }

    function hasChildProperties(node: unknown): boolean {
      const resolved = resolve(node);
      if (!resolved) return false;
      if (resolved.properties && typeof resolved.properties === 'object' &&
          Object.keys(resolved.properties as object).length > 0) return true;
      const options = (resolved.oneOf || resolved.anyOf) as unknown[] | undefined;
      if (Array.isArray(options)) {
        return options.some((opt) => {
          const r = resolve(opt);
          return r?.properties && typeof r.properties === 'object' &&
                 Object.keys(r.properties as object).length > 0;
        });
      }
      return false;
    }

    function extract(node: unknown, prefix: string, depth: number) {
      if (depth > 4) return;
      const properties = collectProperties(node);

      for (const [key, propNode] of Object.entries(properties)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const resolvedProp = resolve(propNode);
        if (!resolvedProp) continue;

        const propType = (resolvedProp.type as string) || 'unknown';
        const isSection = hasChildProperties(propNode);

        variables.push({
          path,
          type: propType,
          isSection,
          description: (resolvedProp.description as string) || undefined,
        });

        if (isSection) {
          extract(propNode, path, depth + 1);
        }
      }
    }

    extract(rootSchema, '', 0);
    return variables;
  } catch {
    return [];
  }
}

/**
 * Extracts Mustache variable full paths from a template string.
 * Resolves section scope: inside {{#data}}...{{/data}}, {{name}} becomes "data.name".
 * Dot-notation variables like {{data.name}} are treated as absolute paths.
 */
function extractMustacheVariables(template: string): string[] {
  const variables = new Set<string>();
  const scopeStack: string[] = [];

  const pattern = /\{\{([#/^]?)([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*|\.)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(template)) !== null) {
    const prefix = match[1];
    const varName = match[2];

    if (prefix === '/') {
      scopeStack.pop();
      continue;
    }
    if (varName === '.') continue;

    let fullPath: string;
    if (varName.includes('.')) {
      fullPath = varName;
    } else {
      const currentScope = scopeStack.length > 0 ? scopeStack[scopeStack.length - 1] : '';
      fullPath = currentScope ? `${currentScope}.${varName}` : varName;
    }

    variables.add(fullPath);

    if (prefix === '#' || prefix === '^') {
      scopeStack.push(fullPath);
    }
  }

  return Array.from(variables).sort();
}

export const AgentFunctionForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentDefinitions, setAgentDefinitions] = useState<{ id: string; name: string }[]>([]);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [inputSchemaJson, setInputSchemaJson] = useState('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState('{}');
  const [systemPrompt, setSystemPrompt] = useState('');

  const title = isEdit ? 'Edit Agent Function' : 'Create Agent Function';

  const [helpOpen, setHelpOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['']));

  const schemaVariables = useMemo(() => extractSchemaVariables(inputSchemaJson), [inputSchemaJson]);
  const allSchemaPaths = useMemo(() => schemaVariables.map((v) => v.path), [schemaVariables]);
  const promptVariables = useMemo(() => extractMustacheVariables(systemPrompt), [systemPrompt]);

  const undefinedVariables = useMemo(
    () => promptVariables.filter((v) => !allSchemaPaths.includes(v)),
    [promptVariables, allSchemaPaths]
  );

  const groupedVariables = useMemo(() => {
    const groups = new Map<string, SchemaVariable[]>();
    for (const v of schemaVariables) {
      const lastDot = v.path.lastIndexOf('.');
      const group = lastDot === -1 ? '' : v.path.substring(0, lastDot);
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(v);
    }
    return groups;
  }, [schemaVariables]);

  const agentDefinitionOptions = useMemo(
    () => [
      { label: 'Select agent...', value: '' },
      ...agentDefinitions.map((a) => ({ label: a.name, value: a.id })),
    ],
    [agentDefinitions]
  );

  const handleInsertVariable = (path: string) => {
    setSystemPrompt((prev) => {
      const tag = `{{${path}}}`;
      if (prev.trim().length === 0) return tag;
      return `${prev} ${tag}`;
    });
  };

  const handleInsertSection = (path: string) => {
    setSystemPrompt((prev) => {
      const block = `{{#${path}}}\n\n{{/${path}}}`;
      if (prev.trim().length === 0) return block;
      return `${prev}\n${block}`;
    });
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  useEffect(() => {
    const loadAgentDefinitions = async () => {
      try {
        const res = await apiClient.get<PagedResult<AgentDefinitionDto>>('/agents?pageSize=100');
        setAgentDefinitions(
          (res.data?.items ?? []).map((a) => ({ id: a.id, name: a.name }))
        );
      } catch (err) {
        console.error('Failed to load agent definitions', err);
        addNotification('Failed to load agent definitions', 'error');
      }
    };
    loadAgentDefinitions();
  }, [addNotification]);

  useEffect(() => {
    if (isEdit && id) {
      loadAgentFunction();
    }
  }, [id, isEdit]);

  const loadAgentFunction = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data: AgentFunctionDetailDto = await agentFunctionService.getAgentFunction(id);
      setName(data.name);
      setSlug(data.slug);
      setDescription(data.description ?? '');
      setAgentDefinitionId(data.agentDefinitionId ?? '');
      setIsActive(data.isActive ?? true);
      setInputSchemaJson(data.inputSchemaJson ?? '{}');
      setOutputSchemaJson(data.outputSchemaJson ?? '{}');
      setSystemPrompt(data.systemPrompt ?? '');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load agent function';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEdit) {
      const generated = generateSlug(name);
      if (generated) setSlug(generated);
    }
  }, [name, isEdit]);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent definition is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await agentFunctionService.updateAgentFunction(id, {
          name: name.trim(),
          description: description.trim() || undefined,
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          systemPrompt: systemPrompt || '',
          isActive,
          agentDefinitionId,
        });
        addNotification('Agent function updated successfully', 'success');
        navigate(`/agent-functions/${id}`);
      } else {
        const created = await agentFunctionService.createAgentFunction({
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          systemPrompt: systemPrompt || '',
          isActive,
          agentDefinitionId,
        });
        addNotification('Agent function created successfully', 'success');
        navigate(`/agent-functions/${created.id}`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save agent function';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEdit && id) {
      navigate(`/agent-functions/${id}`);
    } else {
      navigate('/agent-functions');
    }
  };

  return (
    <PageFormLayout
      title={title}
      subtitle="Define agent functions with input/output schemas and system prompt templates."
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/agent-functions' }}
      actions={{
        onSave: handleSubmit,
        onCancel: handleCancel,
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Zap size={16} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'implementation', label: 'Implementation', icon: <Code size={16} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and agent definition for this function.',
          icon: <Zap size={20} />,
          content: (
            <FormSection
              title="Basic information"
              description="Identify this agent function and link it to an agent definition."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Display name for this agent function."
                  data-testid="agent-function-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="agent-function-name"
                  />
                </FormField>
                <FormField
                  label="Slug"
                  description={isEdit ? 'Slug cannot be changed when editing.' : 'Auto-generated from name.'}
                  data-testid="agent-function-slug"
                >
                  <Input
                    className="w-full"
                    value={slug}
                    onChange={(e) => !isEdit && setSlug(e.target.value)}
                    disabled={isEdit}
                    data-testid="agent-function-slug"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Description" description="Brief summary of what this function does." data-testid="agent-function-description">
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="agent-function-description"
                  />
                </FormField>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Agent Definition"
                  required
                  description="The parent agent this function belongs to."
                  data-testid="agent-function-agentDefinitionId"
                >
                  <CoordioDropdown
                    options={agentDefinitionOptions}
                    value={agentDefinitionId}
                    onChange={(val) => setAgentDefinitionId(val ?? '')}
                    clearable={false}
                    data-testid="agent-function-agentDefinitionId"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'implementation',
          title: 'Implementation',
          description: 'Schemas, system prompt, and activation status.',
          icon: <Code size={20} />,
          content: (
            <FormSection
              title="Schemas &amp; prompt"
              description="Define input/output JSON Schemas and configure the system prompt with Mustache variables."
            >
              <FormField label="Input Schema" description="JSON Schema for input validation." data-testid="agent-function-inputSchemaJson">
                <CodeEditor
                  value={inputSchemaJson}
                  onChange={setInputSchemaJson}
                  language="json"
                  height="220px"
                  data-testid="agent-function-inputSchemaJson"
                />
              </FormField>

              <div className="mt-5">
                <FormField label="Output Schema" description="JSON Schema for output validation." data-testid="agent-function-outputSchemaJson">
                  <CodeEditor
                    value={outputSchemaJson}
                    onChange={setOutputSchemaJson}
                    language="json"
                    height="220px"
                    data-testid="agent-function-outputSchemaJson"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-2 mb-1">
                  <FormField
                    label="System Prompt"
                    description="Use {{variable}} for values, {{#section}}...{{/section}} for nested objects. Click the help icon for a full syntax guide."
                    data-testid="agent-function-systemPrompt"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" onClick={() => setHelpOpen(true)} className="text-[var(--color-accent-primary)]">
                              <HelpIcon size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Mustache variable syntax help</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {groupedVariables.size > 0 && (
                      <div className="mb-1 border border-[var(--color-border-default)] rounded p-3 bg-[var(--color-bg-surface-hover)]">
                        <span className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          Available variables (click to insert):
                        </span>

                        <TooltipProvider>
                          {Array.from(groupedVariables.entries()).map(([group, vars]) => (
                            <div key={group || '__root'} className="mb-2">
                              {group !== '' && (
                                <div
                                  className="flex items-center gap-1 mb-1 cursor-pointer"
                                  onClick={() => toggleGroup(group)}
                                >
                                  {expandedGroups.has(group) ? (
                                    <ExpandLessIcon size={18} className="text-[var(--color-text-secondary)]" />
                                  ) : (
                                    <ExpandMoreIcon size={18} className="text-[var(--color-text-secondary)]" />
                                  )}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {/* Chip.onClick receives no event, so the click is stopped
                                          here instead - otherwise it bubbles to the wrapping div
                                          and toggles the group shut on every insert. */}
                                      <span onClick={(e) => e.stopPropagation()}>
                                        <Chip
                                          label={`${group} (section)`}
                                          icon={<SectionIcon size={14} />}
                                          color="violet"
                                          variant="outlined"
                                          onClick={() => handleInsertSection(group)}
                                          className="font-mono text-xs cursor-pointer"
                                        />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{`Click chip to insert {{#${group}}}...{{/${group}}} section block`}</TooltipContent>
                                  </Tooltip>
                                </div>
                              )}

                              <Collapse in={group === '' || expandedGroups.has(group)}>
                                <div className={`flex flex-wrap gap-1.5 mt-1 ${group ? 'ml-7' : ''}`}>
                                  {vars
                                    .filter((v) => !v.isSection)
                                    .map((v) => {
                                      const isUsed = promptVariables.includes(v.path);
                                      return (
                                        <Tooltip key={v.path}>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <Chip
                                                label={`{{${v.path}}}`}
                                                icon={<CopyIcon size={14} />}
                                                color={isUsed ? 'accent' : 'default'}
                                                variant={isUsed ? 'filled' : 'outlined'}
                                                onClick={() => handleInsertVariable(v.path)}
                                                className="font-mono text-xs cursor-pointer"
                                              />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {isUsed
                                              ? `"{{${v.path}}}" is used in the prompt`
                                              : `Click to insert "{{${v.path}}}" (${v.type})`}
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  {vars
                                    .filter((v) => v.isSection)
                                    .map((v) => (
                                      <Tooltip key={v.path}>
                                        <TooltipTrigger asChild>
                                          <span>
                                            <Chip
                                              label={`{{#${v.path}}}`}
                                              icon={<SectionIcon size={14} />}
                                              color="violet"
                                              variant="outlined"
                                              onClick={() => handleInsertSection(v.path)}
                                              className="font-mono text-xs cursor-pointer"
                                            />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>{`Click to insert {{#${v.path}}}...{{/${v.path}}} section block`}</TooltipContent>
                                      </Tooltip>
                                    ))}
                                </div>
                              </Collapse>
                            </div>
                          ))}
                        </TooltipProvider>
                      </div>
                    )}

                    {undefinedVariables.length > 0 && (
                      <div className="mb-1 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-950">
                        The following variables are used in the System Prompt but not defined in the Input
                        Schema: {undefinedVariables.map((v) => `{{${v}}}`).join(', ')}. These will not be
                        resolved at execution time.
                      </div>
                    )}

                    <CodeEditor
                      value={systemPrompt}
                      onChange={setSystemPrompt}
                      language="plaintext"
                      height="200px"
                      data-testid="agent-function-systemPrompt"
                    />
                  </FormField>
                </div>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="agent-function-isActive" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mustache Syntax Help Dialog */}
              <CortexDialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                title="Mustache Variable Syntax Guide"
                size="lg"
                data-testid="agent-function-help-dialog"
              >
                <CortexDialogBody>
                  <div className="flex flex-col gap-6">
                    <div>
                      <p className="text-sm font-semibold mb-1">Simple Variable</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Insert a value directly from the input data.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
                        {'You are handling a {{entityType}} entity.'}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-1">Dot Notation (Nested Properties)</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Access nested object properties using dots.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
                        {'Contact email: {{data.contact.email}}\nCity: {{data.address.city}}'}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-1">Section Blocks (Nested Objects)</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Use <code>{'{{#section}}...{{/section}}'}</code> to scope into an object.
                        Inside the section, properties resolve relative to that object.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
{`{{#data}}
Entity: {{name}} (License: {{licenseNumber}})
Status: {{status}}
{{#contact}}
Email: {{email}} | Phone: {{phone}}
{{/contact}}
{{/data}}`}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-1">Conditionals</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Sections also act as conditionals: content only renders if the value is truthy.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
{`{{#data.rating}}
Rating: {{data.rating}}
{{/data.rating}}`}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-1">Inverted Sections (Falsy / Missing)</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Use <code>{'{{^variable}}...{{/variable}}'}</code> to render content only when
                        the value is missing, null, or false.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
{`{{^data.contact.website}}
No website provided.
{{/data.contact.website}}`}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-1">Arrays (Iteration)</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        Use a section on an array to iterate. Use <code>{'{{.}}'}</code> for the current item
                        in a simple string/number array.
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
{`Lines of business:
{{#data.linesOfBusiness}}
- {{.}}
{{/data.linesOfBusiness}}`}
                      </pre>
                    </div>

                    <hr className="border-[var(--color-border-default)]" />

                    <div>
                      <p className="text-sm font-semibold mb-1">Full Example (Insurance Schema)</p>
                      <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                        A complete system prompt using multiple Mustache features:
                      </p>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded text-[0.85rem] overflow-auto">
{`You are an insurance {{entityType}} processing agent.

{{#data}}
Entity: {{name}} (ID: {{insurerId}}{{brokerId}})
License: {{licenseNumber}} | Status: {{status}}

{{#contact}}
Contact: {{email}} | {{phone}}
{{#website}}Website: {{website}}{{/website}}
{{/contact}}

{{#address}}
Address: {{line1}}, {{city}}, {{state}} {{postalCode}}, {{country}}
{{/address}}

{{#linesOfBusiness}}
Lines of Business:
{{#linesOfBusiness}}- {{.}}
{{/linesOfBusiness}}
{{/linesOfBusiness}}
{{/data}}

Process the entity data according to the instructions.`}
                      </pre>
                    </div>
                  </div>
                </CortexDialogBody>
                <CortexDialogFooter>
                  <Button type="button" variant="default" onClick={() => setHelpOpen(false)}>
                    Close
                  </Button>
                </CortexDialogFooter>
              </CortexDialog>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
